import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Mic, Loader2, Save, Trash2, Sparkles } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  analyzeAndSaveVoiceProfile,
  deleteVoiceProfile,
  getVoiceProfile,
  setVoiceEnabled,
} from "@/lib/voice.functions";

export function MyVoiceCard() {
  const fetchProfile = useServerFn(getVoiceProfile);
  const analyze = useServerFn(analyzeAndSaveVoiceProfile);
  const toggle = useServerFn(setVoiceEnabled);
  const remove = useServerFn(deleteVoiceProfile);

  const { data, refetch, isLoading } = useQuery({
    queryKey: ["voice-profile"],
    queryFn: () => fetchProfile(),
  });

  const [samples, setSamples] = useState<[string, string, string]>(["", "", ""]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (data?.exists && data.samples) {
      const s = data.samples;
      setSamples([s[0] ?? "", s[1] ?? "", s[2] ?? ""]);
    }
  }, [data]);

  const filled = samples.filter((s) => s.trim().length >= 80);

  async function handleAnalyze() {
    if (filled.length === 0) {
      toast.error("Add at least one sample of at least ~80 characters.");
      return;
    }
    setBusy(true);
    try {
      await analyze({ data: { samples: filled } });
      toast.success("Voice profile saved. New essays will match your style.");
      refetch();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function handleToggle(enabled: boolean) {
    try {
      await toggle({ data: { enabled } });
      refetch();
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function handleDelete() {
    if (!confirm("Delete your saved voice profile? Future essays will use default AI voice.")) return;
    try {
      await remove();
      setSamples(["", "", ""]);
      toast.success("Voice profile deleted.");
      refetch();
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  const profile = data?.exists ? data.profile : null;

  return (
    <Card id="voice">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mic className="h-5 w-5 text-primary" /> My Voice
        </CardTitle>
        <CardDescription>
          Paste 1–3 short samples of your own writing (~100–500 words each — a past essay, journal entry, or email).
          SmartEssay will learn your style and match it in every essay you generate.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {data?.exists && profile && (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm">
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 font-medium">
                    <Sparkles className="h-4 w-4 text-primary" />
                    Voice profile active
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{profile.summary}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Label htmlFor="voice-enabled" className="text-xs">Apply to new essays</Label>
                  <Switch
                    id="voice-enabled"
                    checked={data.enabled}
                    onCheckedChange={handleToggle}
                  />
                </div>
              </div>
            )}

            {[0, 1, 2].map((i) => (
              <div key={i} className="space-y-1.5">
                <Label htmlFor={`sample-${i}`} className="text-sm">
                  Writing sample {i + 1} {i === 0 ? "(required)" : "(optional)"}
                </Label>
                <Textarea
                  id={`sample-${i}`}
                  value={samples[i]}
                  onChange={(e) => {
                    const next = [...samples] as [string, string, string];
                    next[i] = e.target.value;
                    setSamples(next);
                  }}
                  placeholder={
                    i === 0
                      ? "Paste a past essay, journal entry, blog post, or long email you wrote yourself…"
                      : "Add another sample for a more accurate match…"
                  }
                  className="min-h-[120px] font-mono text-xs"
                  maxLength={8000}
                />
                <p className="text-xs text-muted-foreground">
                  {samples[i].trim().length} chars {samples[i].trim().length >= 80 ? "✓" : "(need ~80+)"}
                </p>
              </div>
            ))}

            <div className="flex flex-wrap gap-2">
              <Button onClick={handleAnalyze} disabled={busy || filled.length === 0}>
                {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />}
                {data?.exists ? "Re-analyze & update" : "Analyze & save voice"}
              </Button>
              {data?.exists && (
                <Button variant="outline" onClick={handleDelete} disabled={busy}>
                  <Trash2 className="mr-1.5 h-4 w-4" /> Delete profile
                </Button>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
